/*
  Miamon — carrusel de reels (coverflow).

  Portado del componente de la web en WordPress. Cada diapositiva lleva un
  `data-pos` (0 = centro, ±1, ±2, ±3 = fuera) y el CSS resuelve posición,
  escala y brillo. Aquí solo se calcula qué posición le toca a cada una.

  Es un custom element para que funcione con varias instancias y con el
  editor de temas, que reinyecta secciones sin recargar la página.
*/
if (!customElements.get('mm-reels')) {
  customElements.define(
    'mm-reels',
    class MmReels extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('.mm-reels__stage');
        this.slides = Array.from(this.querySelectorAll('.mm-reel'));
        this.dots = Array.from(this.querySelectorAll('[data-mm-reels-goto]'));
        if (this.slides.length === 0) return;

        this.count = this.slides.length;
        this.current = 0;
        this.timer = null;
        this.delay = Number(this.dataset.delay) || 5000;
        this.wantsAutoplay = this.dataset.autoplay === 'true';
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.bindControls();
        this.bindDrag();
        this.update();
        this.startAuto();

        // Pausar cuando la sección no está a la vista: no tiene sentido
        // rotar vídeos que nadie ve, y ahorra batería.
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => {
              entries.forEach((e) => (e.isIntersecting ? this.startAuto() : this.stopAuto()));
            },
            { threshold: 0.25 }
          );
          this.io.observe(this);
        }
      }

      disconnectedCallback() {
        this.stopAuto();
        if (this.io) this.io.disconnect();
      }

      /* Distancia circular con signo entre el índice i y el actual,
         para que el salto entre la última y la primera sea corto. */
      offsetOf(i) {
        var half = Math.floor(this.count / 2);
        var diff = ((i - this.current) % this.count + this.count) % this.count;
        return diff > half ? diff - this.count : diff;
      }

      update() {
        this.slides.forEach((slide, i) => {
          var off = this.offsetOf(i);
          var pos = Math.max(-3, Math.min(3, off));
          slide.dataset.pos = String(pos);

          var video = slide.querySelector('video');
          if (video) {
            if (pos === 0) {
              video.play().catch(function () {});
            } else {
              // Las laterales siguen animadas pero siempre en silencio.
              video.muted = true;
              var btn = slide.querySelector('[data-mm-sound]');
              if (btn) btn.dataset.muted = '1';
              video.play().catch(function () {});
            }
          }

          var link = slide.querySelector('.mm-reel__link');
          if (link) link.tabIndex = pos === 0 ? 0 : -1;
        });

        this.dots.forEach((dot, i) => {
          dot.setAttribute('aria-current', i === this.current ? 'true' : 'false');
        });
      }

      goTo(index) {
        this.current = ((index % this.count) + this.count) % this.count;
        this.update();
        this.resetAuto();
      }

      next() {
        this.goTo(this.current + 1);
      }

      prev() {
        this.goTo(this.current - 1);
      }

      startAuto() {
        if (!this.wantsAutoplay || this.reduced || this.count < 2) return;
        this.stopAuto();
        this.timer = setInterval(this.next.bind(this), this.delay);
      }

      stopAuto() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }

      resetAuto() {
        if (!this.timer) return;
        this.startAuto();
      }

      bindControls() {
        this.querySelectorAll('[data-mm-reels-dir]').forEach((btn) => {
          btn.addEventListener('click', () => {
            Number(btn.dataset.mmReelsDir) > 0 ? this.next() : this.prev();
          });
        });

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goTo(Number(dot.dataset.mmReelsGoto)));
        });

        this.slides.forEach((slide, i) => {
          slide.addEventListener('click', (event) => {
            // Una tarjeta lateral no navega: primero se centra.
            if (slide.dataset.pos === '0') return;
            event.preventDefault();
            this.goTo(i);
          });
        });

        this.querySelectorAll('[data-mm-sound]').forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            var video = btn.closest('.mm-reel__card').querySelector('video');
            if (!video) return;
            video.muted = !video.muted;
            btn.dataset.muted = video.muted ? '1' : '0';
            if (!video.muted) video.play().catch(function () {});
          });
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.next();
          if (event.key === 'ArrowLeft') this.prev();
        });
      }

      bindDrag() {
        // Gesto compartido con el hero y los testimonios (assets/mm-drag.js).
        // El de antes era propio y no llevaba `touch-action`, así que en móvil
        // el navegador se quedaba el gesto y nunca llegaba a arrastrar.
        if (!this.stage || !window.mmArrastre) return;
        window.mmArrastre(this.stage, (dir) => {
          dir > 0 ? this.next() : this.prev();
        });
      }
    }
  );
}
